import React, { useCallback, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { useDispatch, useSelector } from "react-redux";

import { patientActions } from "../../actions/PatientActions";
import { useApi } from "../../contexts/ApiProvider";
import { buildConstraintSeqIds, srceSeqIds } from "../../utils/utils";
import { fetchTreeLeaves } from "../../utils/phyloFetch";

const LinkIcon = ({ active }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-link-45deg" viewBox="0 0 16 16">
    <path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1 1 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4 4 0 0 1-.128-1.287z"/>
    <path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243z"/>
  </svg>
);

export default function OverlapTreeLinkToggle() {
  const dispatch = useDispatch();
  const api = useApi();

  const patient = useSelector((state) => state.patient.patient);
  const microId = useSelector((state) => state.patient.micro_id);
  const overlapConstraint = useSelector(
    (state) => state.patient.tree.overlap_constraint
  );
  const constraintLoading = useSelector(
    (state) => state.patient.tree.constraint_loading
  );
  const treeData = useSelector((state) => state.patient.tree.tree);
  const snpDistance = useSelector((state) => state.patient.snp_distance);

  const indexSeqId = patient?.pt?.seq_id ?? null;
  const admissionMissing = patient?.pt?.adm_start === null;

  const sourceSeqIds = useMemo(() => {
    if (!patient || !Array.isArray(patient.source_list)) {
      return [];
    }
    return srceSeqIds(patient);
  }, [patient]);

  const constraintSeqIds = useMemo(
    () => buildConstraintSeqIds(sourceSeqIds, indexSeqId),
    [sourceSeqIds, indexSeqId]
  );

  const treeUnavailable =
    treeData === undefined ||
    (treeData && typeof treeData === "object" && treeData.tree === null);

  const canConstrain =
    constraintSeqIds.length > 0 && !admissionMissing && !treeUnavailable;

  const handleDisable = useCallback(() => {
    dispatch(patientActions.constrainTree([]));
    dispatch(patientActions.constrainOverlap([]));
    dispatch(patientActions.constrainByOverlap(false));
  }, [dispatch]);

  const handleEnable = useCallback(async () => {
    if (!canConstrain || !microId) {
      return;
    }

    dispatch(patientActions.constraintLoadingStart());
    dispatch(patientActions.constrainByOverlap(true));
    dispatch(patientActions.constrainTree(constraintSeqIds));
    try {
      const constraints = await fetchTreeLeaves(api, microId, snpDistance);
      dispatch(patientActions.constrainOverlap(constraints || []));
    } catch (error) {
      console.error("Error enabling overlap/tree linkage:", error);
      dispatch(patientActions.constrainTree([]));
      dispatch(patientActions.constrainByOverlap(false));
      dispatch(patientActions.constrainOverlap([]));
    } finally {
      dispatch(patientActions.constraintLoadingEnd());
    }
  }, [
    api,
    canConstrain,
    constraintSeqIds,
    dispatch,
    microId,
    snpDistance,
  ]);

  const handleToggle = useCallback(() => {
    if (constraintLoading) {
      return;
    }
    if (overlapConstraint) {
      handleDisable();
    } else {
      void handleEnable();
    }
  }, [constraintLoading, handleDisable, handleEnable, overlapConstraint]);

  // const buttonDisabled = constraintLoading || !canConstrain;
  const buttonDisabled = !canConstrain;
  const buttonTitle = overlapConstraint
    ? "Disable linkage between overlap and tree views"
    : canConstrain
    ? "Link overlap and tree views"
    : "Linkage unavailable until both views have comparable sequence data";

  return (
    <Button
      size="sm"
      variant="outline-secondary"
      className={`linkage-toggle-button${
        overlapConstraint ? " linkage-toggle-button--active" : ""
      }`}
      onClick={handleToggle}
      disabled={buttonDisabled}
      aria-pressed={overlapConstraint}
      aria-label={buttonTitle}
      title={buttonTitle}
    >
      {/* {constraintLoading ? (
        <Spinner
          animation="border"
          role="status"
          size="sm"
          style={{ width: "1.25rem", height: "1.25rem" }}
        />
      ) : ( */}
        <LinkIcon active={overlapConstraint} />
      {/* )} */}
    </Button>
  );
}
